/**
 * Display-only email quote stripping (CRM Unibox email renderer).
 *
 * Inbound email replies carry the full quoted history inside a single
 * message body (Gmail/Outlook append the previous thread). Rendering it
 * verbatim makes every reply look like one huge pasted block. These helpers
 * return ONLY the latest reply for display, with a flag indicating quoted
 * history was hidden.
 *
 * IMPORTANT — display only:
 *   - Never mutates the stored message. Callers pass a copy of bodyText /
 *     bodyHtml; the original CommunicationMessage row is untouched.
 *   - Used ONLY by the email branch of ThreadMessageRenderer. SMS / WhatsApp
 *     / chat / call renderers do NOT call these.
 *   - A "Show quoted text" toggle can re-render the untouched original, so
 *     nothing is ever lost.
 */

export interface StrippedText {
  /** The latest reply only (quoted history removed). */
  visible: string;
  /** True when quoted history was detected and hidden. */
  hasQuoted: boolean;
}

export interface StrippedHtml {
  visibleHtml: string;
  hasQuoted: boolean;
}

/** Gmail attribution line, e.g. `On Mon, Jul 8, 2026 at 10:00 AM John <j@x> wrote:` */
function isGmailAttribution(lines: string[], i: number): boolean {
  const line = lines[i].trim();
  if (/^On\b.+\bwrote:?\s*$/.test(line)) return true;
  // Long attributions wrap across two lines: "On …" then "… wrote:"
  if (/^On\b/.test(line) && !/wrote:?\s*$/.test(line)) {
    const joined = `${line} ${(lines[i + 1] || '').trim()}`.trim();
    if (/^On\b.+\bwrote:?\s*$/.test(joined)) return true;
  }
  return false;
}

/** Outlook "-----Original Message-----" divider. */
function isOutlookOriginal(line: string): boolean {
  return /^-{2,}\s*Original Message\s*-{2,}/i.test(line.trim());
}

/** Outlook long underscore divider that precedes the quoted header. */
function isOutlookDivider(line: string): boolean {
  return /^_{5,}\s*$/.test(line.trim());
}

/** A quoted line (`> …`). */
function isQuotedLine(line: string): boolean {
  return /^\s*>/.test(line);
}

/**
 * Outlook quoted header cluster without the divider:
 *   From: …
 *   Sent: …   (or Date:)
 *   To: …
 *   Subject: …
 * Only treated as a boundary when a `From:` header line is followed shortly
 * by another header line, so a stray "From: the team" in prose won't trip it.
 */
function isOutlookHeaderCluster(lines: string[], i: number): boolean {
  if (!/^\s*From:\s*\S/i.test(lines[i])) return false;
  const window = lines.slice(i + 1, i + 6).map((l) => l.trim());
  return window.some((l) => /^(Sent|Date|To|Subject|Cc):\s*\S/i.test(l));
}

function isQuoteBoundary(lines: string[], i: number): boolean {
  const line = lines[i];
  return (
    isQuotedLine(line) ||
    isGmailAttribution(lines, i) ||
    isOutlookOriginal(line) ||
    isOutlookDivider(line) ||
    isOutlookHeaderCluster(lines, i)
  );
}

/**
 * Return the latest reply from a plaintext email body. Cuts at the first
 * quote boundary (Gmail "On … wrote:", Outlook original/header, `>` lines).
 */
export function stripQuotedEmailForDisplay(body: string | null | undefined): StrippedText {
  if (!body) return { visible: '', hasQuoted: false };
  const lines = body.replace(/\r\n/g, '\n').split('\n');

  let cut = -1;
  for (let i = 0; i < lines.length; i++) {
    if (isQuoteBoundary(lines, i)) {
      cut = i;
      break;
    }
  }
  if (cut === -1) return { visible: body.trim(), hasQuoted: false };

  const visible = lines.slice(0, cut).join('\n').trim();
  return { visible, hasQuoted: true };
}

// Quote containers Gmail/Outlook/Apple Mail wrap the previous thread in.
const HTML_QUOTE_SELECTORS = [
  'blockquote.gmail_quote',
  'div.gmail_quote',
  '.gmail_extra',
  'blockquote[type="cite"]',
  '#divRplyFwdMsg', // Outlook web
  '#appendonsend',
  '.OutlookMessageHeader',
  'blockquote', // generic email quote (last resort; recoverable via toggle)
];

/**
 * Return the latest reply from an HTML email body by removing the known
 * quote containers. Uses DOMParser (browser/jsdom); on any environment
 * without it, returns the input unchanged (no stripping, never throws).
 */
export function stripQuotedEmailHtmlForDisplay(html: string | null | undefined): StrippedHtml {
  if (!html) return { visibleHtml: '', hasQuoted: false };
  if (typeof DOMParser === 'undefined') {
    return { visibleHtml: html, hasQuoted: false };
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let removed = 0;
  for (const selector of HTML_QUOTE_SELECTORS) {
    doc.querySelectorAll(selector).forEach((el) => {
      el.remove();
      removed += 1;
    });
  }
  const visibleHtml = doc.body.innerHTML.trim();
  return { visibleHtml, hasQuoted: removed > 0 };
}
